import { ApplicationConfig, provideZoneChangeDetection } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import { ConfirmationService, MessageService } from 'primeng/api';
import Aura from '@primeng/themes/aura';
import { definePreset } from '@primeng/themes';

import { routes } from './app.routes';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { responseInterceptor } from './core/interceptors/response.interceptor';
import { errorInterceptor } from './core/interceptors/error.interceptor';

// Midnight — dark theme. PrimeNG's own "dark" colorScheme branch already has the
// right role structure (formField.background, content.background, text.color, etc.
// each point at a numbered surface stop) — we just replace the stop values with our
// palette instead of the stock zinc scale, and force that branch on unconditionally
// via darkModeSelector below rather than following the OS/class toggle.
const AppTheme = definePreset(Aura, {
  semantic: {
    primary: {
      50: '{indigo.50}',
      100: '{indigo.100}',
      200: '{indigo.200}',
      300: '{indigo.300}',
      400: '{indigo.400}',
      500: '{indigo.500}',
      600: '{indigo.600}',
      700: '{indigo.700}',
      800: '{indigo.800}',
      900: '{indigo.900}',
      950: '{indigo.950}',
    },
    colorScheme: {
      dark: {
        surface: {
          0: '#eef0fa',
          50: '#eef0fa',
          100: '#dfe1ee',
          200: '#c7cade',
          300: '#b4b7d1',
          400: '#9296b8',
          500: '#6b6f8f',
          600: '#3a3f5c',
          700: '#2a2e46',
          800: '#1c1f33',
          900: '#151726',
          950: '#1b1e30',
        },
      },
    },
  },
  components: {
    button: {
      root: {
        borderRadius: '0.75rem',
      },
      colorScheme: {
        dark: {
          root: {
            primary: {
              background: 'linear-gradient(135deg, #6366f1 0%, #0ea5e9 100%)',
              hoverBackground: 'linear-gradient(135deg, #818cf8 0%, #38bdf8 100%)',
              activeBackground: 'linear-gradient(135deg, #4f46e5 0%, #0284c7 100%)',
              borderColor: 'transparent',
              hoverBorderColor: 'transparent',
              activeBorderColor: 'transparent',
            },
          },
        },
      },
    },
  },
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideHttpClient(withInterceptors([authInterceptor, responseInterceptor, errorInterceptor])),
    provideAnimationsAsync(),
    MessageService,
    ConfirmationService,
    providePrimeNG({
      theme: {
        preset: AppTheme,
        options: {
          // Permanently on — see the ".p-dark" class on <html> in index.html.
          // The app has no light/dark toggle; Midnight is the only theme.
          darkModeSelector: '.p-dark',
        },
      },
    }),
  ],
};
